import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import {
  rushTourModeAtom,
  kothTourModeAtom,
  championshipTourModeAtom,
} from "@/lib/gameStatusAtom";
import {
  NextStep,
  NextStepProvider,
  useNextStep,
  type Step,
  type Tour,
} from "nextstepjs";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { usePrivy } from "@privy-io/react-auth";
import TourCard from "./TourCard";
import { TourLayoutRefreshContext } from "./nextstep-bridge";
import { isReplayActive, setReplayActive } from "@/lib/analytics/onboarding";
import { t } from "@/lib/i18n";

// Re-export useNextStep for convenience
// eslint-disable-next-line react-refresh/only-export-components
export { useNextStep };

// Internal component to handle tour logic with access to useNextStep
function TourSteps({
  children,
  navigate,
  steps,
}: {
  children: React.ReactNode;
  navigate: ReturnType<typeof useNavigate>;
  steps: Tour[];
}) {
  const { closeNextStep, currentTour } = useNextStep();
  const { authenticated, login, user } = usePrivy();
  const router = useRouter();
  const currentStepRef = useRef<number | null>(null);
  const [tourLayoutRefreshVersion, setTourLayoutRefreshVersion] = useState(0);
  const setSuspendTour = useCallback(
    (suspend: boolean) => {
      if (suspend) closeNextStep();
    },
    [closeNextStep],
  );
  const stepsByTour = useMemo(() => {
    const map = new Map<string, Step[]>();
    steps.forEach((tour) => {
      map.set(tour.tour, tour.steps);
    });
    return map;
  }, [steps]);
  const refreshTourLayout = useCallback(() => {
    setTourLayoutRefreshVersion((previous) => previous + 1);
  }, []);

  const bridgedSteps = useMemo(() => {
    if (tourLayoutRefreshVersion === 0) return steps;
    return steps.map((tour) => ({ ...tour, steps: [...tour.steps] }));
  }, [steps, tourLayoutRefreshVersion]);

  // useEffect(() => {
  //   if (currentTour !== "practiceCompletionTour") {
  //     practiceCompletionHandledRef.current = false;
  //   }
  // }, [currentTour]);

  // const handlePracticeCompletionAdvance = useCallback(() => {
  //   if (practiceCompletionHandledRef.current) return;
  //   practiceCompletionHandledRef.current = true;
  //
  //   console.log("[Tour] Navigating to home after practice completion");
  //   setSuspendTour(true);
  //   closeNextStep();
  //   localStorage.setItem("cfl:practice-complete-tour-done", "true");
  //   sessionStorage.setItem("startHomeModesTour", "true");
  //
  //   setTimeout(() => {
  //     navigate({ to: "/home" });
  //   }, 200);
  // }, [closeNextStep, navigate]);

  const handleStepChange = useCallback(
    (stepIndex: number) => {
      // homeTour: Step 0 - Basecamp hotspot -> Step 1 should navigate to /basecamp
      // basecampTour: Step 0 - Practice Mode button -> Step 1 should navigate to /practice
      // practiceTour: Steps 0-3 - Practice page elements (no navigation)
      currentStepRef.current = stepIndex;

      console.log(
        "[Tour] Step change:",
        stepIndex,
        "Current path:",
        window.location.pathname,
      );

      // if (currentTour === "practiceCompletionTour" && stepIndex === 3) {
      //   handlePracticeCompletionAdvance();
      //   return;
      // }

      // const isReplay = isReplayActive();
      // const onboardingTours = new Set([
      //   "onboardingTour",
      //   // "homeTour",
      //   "basecampTour",
      //   "practiceTour",
      //   "practiceDirectionTour",
      //   "practiceCompletionTour",
      //   "homeModesTour",
      //   "practiceLiveTour",
      // ]);

      // if (currentTour && onboardingTours.has(currentTour) && !isReplay) {
      //   const normalizedTour = normalizeTourName(currentTour);
      //   const stepsForTour = stepsByTour.get(currentTour);
      //   const step =
      //     stepsForTour && stepIndex >= 0 ? stepsForTour[stepIndex] : undefined;

      //   // if (stepIndex === 1 && currentTour === "homeTour") {
      //   //   trackOnboardingEvent(
      //   //     "onboarding_start",
      //   //     {
      //   //       tour: normalizedTour,
      //   //       stepIndex,
      //   //       stepId: (step as Step | undefined)?.selector ?? null,
      //   //       authStatus: authenticated ? "authenticated" : "unauthenticated",
      //   //       userId: user?.id,
      //   //     },
      //   //     {
      //   //       oncePerSession: true,
      //   //       sessionKey: `cfl:onboarding-start:${normalizedTour}`,
      //   //     },
      //   //   );
      //   // }

      //   trackOnboardingEvent(
      //     "onboarding_step_view",
      //     {
      //       tour: normalizedTour,
      //       stepIndex,
      //       stepId: (step as Step | undefined)?.selector ?? null,
      //       authStatus: authenticated ? "authenticated" : "unauthenticated",
      //       userId: user?.id,
      //     },
      //     {
      //       oncePerSession: true,
      //       sessionKey: `cfl:onboarding-view:${normalizedTour}:${stepIndex}`,
      //     },
      //   );
      // }

      // lobbySquadTour: step 2 → open equip sheet so token list/direction/equip are visible
      if (currentTour === "lobbySquadTour" && stepIndex === 2) {
        window.dispatchEvent(new CustomEvent("lobbySquadTourOpenEquip"));
      }

      // lobbyWelcomeTour: step 2 → open modes dialog so practice card is visible
      if (
        currentTour === "lobbyWelcomeTour" &&
        stepIndex === 2
      ) {
        window.dispatchEvent(new CustomEvent("lobbyWelcomeTourOpenModes"));
      }

      // lobbyWelcomeTour: step 3 (empty sentinel) → navigate to practice
      if (
        currentTour === "lobbyWelcomeTour" &&
        stepIndex === 3 &&
        window.location.pathname === "/home"
      ) {
        console.log("[Tour] Navigating from lobby to practice (lobby welcome tour)");
        setSuspendTour(true);
        closeNextStep();
        localStorage.setItem("cfl:lobby-welcome-tour-done", "true");
        sessionStorage.setItem("cfl:lobby-tour-practice-pending", "true");
        setTimeout(() => {
          navigate({ to: "/practice" });
        }, 200);
        return;
      }

      // lobbyWelcomeTour: step 4 (back-to-home) — already on practice ended page, just let it render
      // When user clicks the button, navigateAfterPractice handles navigation to /home

      // Handle navigation from home to basecamp (homeTour step 0 -> step 1)
      // if (
      //   currentTour === "homeTour" &&
      //   stepIndex === 2 &&
      //   window.location.pathname === "/home"
      // ) {
      //   console.log("[Tour] Navigating from home to basecamp");
      //   setSuspendTour(true);
      //   closeNextStep();
      //   sessionStorage.setItem("continueTourOnBasecamp", "true");
      //   setTimeout(() => {
      //     navigate({ to: "/home" });
      //   }, 200);
      //   return;
      // }

      // Handle navigation from basecamp to practice (basecampTour step 0 -> step 1)
      // if (
      //   currentTour === "basecampTour" &&
      //   stepIndex === 1 &&
      //   window.location.pathname === "/home"
      // ) {
      //   console.log("[Tour] Navigating from basecamp to practice");
      //   // Close the tour before navigation
      //   setSuspendTour(true);
      //   closeNextStep();
      //
      //   // Mark that we're navigating to practice
      //   sessionStorage.setItem("continueTourOnPractice", "true");
      //
      //   // Navigate to practice
      //   setTimeout(() => {
      //     navigate({ to: "/practice" });
      //   }, 200);
      //   return;
      // }
    },
    [
      navigate,
      closeNextStep,
      currentTour,
      // handlePracticeCompletionAdvance,
      authenticated,
      stepsByTour,
      user?.id,
    ],
  );

  // Close any active tour on route change
  const prevPathRef = useRef(router.state.location.pathname);
  useEffect(() => {
    const path = router.state.location.pathname;
    if (prevPathRef.current !== path) {
      prevPathRef.current = path;
      closeNextStep();
    }
  }, [router.state.location.pathname, closeNextStep]);

  const handleSkip = useCallback(() => {
    console.log("[Tour] User skipped onboarding tour");
    // const onboardingTours = new Set([
    //   "onboardingTour",
    //   "homeTour",
    //   "basecampTour",
    //   "practiceTour",
    //   "practiceDirectionTour",
    //   "practiceCompletionTour",
    //   "homeModesTour",
    //   "practiceLiveTour",
    // ]);

    const isReplay = isReplayActive();

    // if (currentTour && onboardingTours.has(currentTour)) {
    //   if (isReplay) {
    //     trackOnboardingEvent("onboarding_replay_end", {
    //       tour: normalizeTourName(currentTour),
    //       stepIndex: currentStepRef.current,
    //       authStatus: authenticated ? "authenticated" : "unauthenticated",
    //       userId: user?.id,
    //       reason: "skip",
    //       replay: true,
    //     });
    //   } else {
    //     trackOnboardingEvent(
    //       "onboarding_skip",
    //       {
    //         tour: normalizeTourName(currentTour),
    //         stepIndex: currentStepRef.current,
    //         authStatus: authenticated ? "authenticated" : "unauthenticated",
    //         userId: user?.id,
    //       },
    //       {
    //         oncePerSession: true,
    //         sessionKey: "cfl:onboarding-skip-sent",
    //       },
    //     );
    //     localStorage.setItem("onboardingCompleted", "true");
    //   }
    // }
    if (currentTour === "lobbyWelcomeTour") {
      localStorage.setItem("cfl:lobby-welcome-tour-done", "true");
    }
    if (currentTour === "lobbySquadTour") {
      localStorage.setItem("cfl:lobby-squad-tour-done", "true");
    }
    if (currentTour === "createSquadTour") {
      localStorage.setItem("cfl:create-squad-tour-done", "true");
    }
    if (currentTour === "rushWaitingTour") {
      localStorage.setItem("cfl:rush-waiting-tour-done", "true");
    }
    if (currentTour === "wtaTour") {
      localStorage.setItem("cfl:wta-tour-done", "true");
    }
    if (currentTour === "luckyDrawTour") {
      localStorage.setItem("cfl:lucky-draw-tour-done", "true");
    }
    if (currentTour === "vipProgramTour") {
      localStorage.setItem("cfl:vip-program-tour-done", "true");
    }
    if (currentTour === "kothWaitingTour") {
      localStorage.setItem("cfl:koth-waiting-tour-done", "true");
    }
    if (currentTour === "alphaDomeTour") {
      localStorage.setItem("cfl:alpha-dome-tour-done", "true");
    }
    if (currentTour === "championshipInactiveTour") {
      localStorage.setItem("cfl:championship-inactive-tour-done", "true");
    }
    if (currentTour === "championshipTour") {
      localStorage.setItem("cfl:championship-tour-done", "true");
    }
    // if (currentTour === "practiceCompletionTour") {
    //   localStorage.setItem("cfl:practice-complete-tour-done", "true");
    //   sessionStorage.setItem("startHomeModesTour", "true");
    // }
    // if (currentTour === "homeModesTour") {
    //   localStorage.setItem("onboardingCompleted", "true");
    //   localStorage.setItem("cfl:home-modes-tour-done", "true");
    //   sessionStorage.removeItem("startHomeModesTour");
    //   navigate({ to: "/rush" });
    //   if (!authenticated) {
    //     try {
    //       login();
    //     } catch (error) {
    //       console.warn(
    //         "[Tour] Privy login after home modes tour failed",
    //         error,
    //       );
    //     }
    //   }
    // }
    // if (currentTour === "practiceLiveTour") {
    //   localStorage.setItem("cfl:practice-live-tour-done", "true");
    //   sessionStorage.removeItem("continuePracticeLiveTour");
    // }
    if (currentTour === "rushExtraTrophyTour") {
      localStorage.setItem("cfl:rush-extra-trophy-tour-skipped", "true");
    }
    if (currentTour === "grandRushExtraTrophyTour") {
      localStorage.setItem("cfl:grand-rush-extra-trophy-tour-skipped", "true");
    }
    if (currentTour === "questPanelTour") {
      localStorage.setItem("cfl:quest-panel-tour-done", "true");
    }
    if (currentTour === "welcomeProTour") {
      const tourKey = user?.id
        ? `cfl:welcome-pro-tour-done:${user.id}`
        : "cfl:welcome-pro-tour-done";
      localStorage.setItem(tourKey, "true");
      window.dispatchEvent(new CustomEvent("welcomeProTourDone"));
    }

    sessionStorage.removeItem("continueTourOnBasecamp");
    sessionStorage.removeItem("continueTourOnPractice");
    if (isReplay) {
      setReplayActive(false);
    }
  }, [authenticated, currentTour, login, navigate, user?.id]);

  const handleComplete = useCallback(() => {
    console.log("[Tour] User completed onboarding tour");
    // if (currentTour === "practiceCompletionTour") {
    //   handlePracticeCompletionAdvance();
    // }

    if (currentTour === "lobbyWelcomeTour") {
      localStorage.setItem("cfl:lobby-welcome-tour-done", "true");
    }
    if (currentTour === "lobbySquadTour") {
      localStorage.setItem("cfl:lobby-squad-tour-done", "true");
    }
    if (currentTour === "createSquadTour") {
      localStorage.setItem("cfl:create-squad-tour-done", "true");
    }
    if (currentTour === "rushWaitingTour") {
      localStorage.setItem("cfl:rush-waiting-tour-done", "true");
    }
    if (currentTour === "wtaTour") {
      localStorage.setItem("cfl:wta-tour-done", "true");
    }
    if (currentTour === "luckyDrawTour") {
      localStorage.setItem("cfl:lucky-draw-tour-done", "true");
    }
    if (currentTour === "vipProgramTour") {
      localStorage.setItem("cfl:vip-program-tour-done", "true");
    }
    if (currentTour === "kothWaitingTour") {
      localStorage.setItem("cfl:koth-waiting-tour-done", "true");
    }
    if (currentTour === "alphaDomeTour") {
      localStorage.setItem("cfl:alpha-dome-tour-done", "true");
    }
    if (currentTour === "championshipInactiveTour") {
      localStorage.setItem("cfl:championship-inactive-tour-done", "true");
    }
    if (currentTour === "championshipTour") {
      localStorage.setItem("cfl:championship-tour-done", "true");
    }
    if (currentTour === "questPanelTour") {
      localStorage.setItem("cfl:quest-panel-tour-done", "true");
    }
    if (currentTour === "welcomeProTour") {
      const tourKey = user?.id
        ? `cfl:welcome-pro-tour-done:${user.id}`
        : "cfl:welcome-pro-tour-done";
      localStorage.setItem(tourKey, "true");
      window.dispatchEvent(new CustomEvent("welcomeProTourDone"));
    }
    // if (currentTour === "practiceCompletionTour") {
    //   localStorage.setItem("cfl:practice-complete-tour-done", "true");
    //   sessionStorage.setItem("startHomeModesTour", "true");
    // }
    // if (currentTour === "homeModesTour") {
    //   if (isReplay) {
    //     trackOnboardingEvent("onboarding_replay_end", {
    //       tour: "home_modes",
    //       stepIndex: currentStepRef.current,
    //       authStatus: authenticated ? "authenticated" : "unauthenticated",
    //       userId: user?.id,
    //       reason: "complete",
    //       replay: true,
    //     });
    //   } else {
    //     const alreadyCompleted =
    //       localStorage.getItem("onboardingCompleted") === "true";
    //     if (!alreadyCompleted) {
    //       trackOnboardingEvent(
    //         "onboarding_complete",
    //         {
    //           tour: "home_modes",
    //           stepIndex: currentStepRef.current,
    //           authStatus: authenticated ? "authenticated" : "unauthenticated",
    //           userId: user?.id,
    //         },
    //         {
    //           oncePerSession: true,
    //           sessionKey: "cfl:onboarding-complete-sent",
    //         },
    //       );
    //     }
    //     localStorage.setItem("onboardingCompleted", "true");
    //   }
    //
    //   localStorage.setItem("cfl:home-modes-tour-done", "true");
    //   sessionStorage.removeItem("startHomeModesTour");
    //   setReplayActive(false);
    //   navigate({ to: "/rush" });
    //   if (!authenticated) {
    //     try {
    //       login();
    //     } catch (error) {
    //       console.warn(
    //         "[Tour] Privy login after home modes tour failed",
    //         error,
    //       );
    //     }
    //   }
    // }
    // if (currentTour === "practiceLiveTour") {
    //   localStorage.setItem("cfl:practice-live-tour-done", "true");
    //   sessionStorage.removeItem("continuePracticeLiveTour");
    // }

    sessionStorage.removeItem("continueTourOnBasecamp");
    sessionStorage.removeItem("continueTourOnPractice");
  }, [
    authenticated,
    currentTour,
    // handlePracticeCompletionAdvance,
    login,
    navigate,
    user?.id,
  ]);

  return (
    <TourLayoutRefreshContext.Provider
      value={{ refreshTourLayout, setSuspendTour }}
    >
      <NextStep
        clickThroughOverlay={false}
        cardComponent={TourCard}
        shadowOpacity="0.75"
        scrollToTop={false}
        noInViewScroll={true}
        steps={bridgedSteps}
        onStepChange={(step) => {
          if (step !== undefined && step !== null) {
            handleStepChange(step);
          }
        }}
        onSkip={handleSkip}
        onComplete={handleComplete}
      >
        {children}
      </NextStep>
    </TourLayoutRefreshContext.Provider>
  );
}

interface TourProviderProps {
  children: React.ReactNode;
  onComplete?: () => void;
  className?: string;
  isTourCompleted?: boolean;
}

export function TourProvider({ children }: TourProviderProps) {
  const navigate = useNavigate();
  const rushTourMode = useAtomValue(rushTourModeAtom);
  const kothTourMode = useAtomValue(kothTourModeAtom);
  const championshipTourMode = useAtomValue(championshipTourModeAtom);

  const steps: Tour[] = [
    // {
    //   tour: "onboardingTour",
    //   steps: [
    //     {
    //       selector: "[data-step='welcome']",
    //       title: t("tours.onboardingTour.steps.welcome.title"),
    //       content: t("tours.onboardingTour.steps.welcome.content"),
    //       side: "bottom",
    //       hideControls: false,
    //       pointerPadding: 6,
    //       pointerRadius: 10,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //     {
    //       selector: "[data-step='wallet']",
    //       title: t("tours.onboardingTour.steps.wallet.title"),
    //       content: t("tours.onboardingTour.steps.wallet.content"),
    //       side: "top",
    //       hideControls: false,
    //       pointerPadding: 4,
    //       pointerRadius: 10,
    //       icon: "",
    //     } as Step,
    //     {
    //       selector: "[data-step='game-info']",
    //       title: t("tours.onboardingTour.steps.gameInfo.title"),
    //       content: t("tours.onboardingTour.steps.gameInfo.content"),
    //       side: "left",
    //       hideControls: false,
    //       pointerPadding: 4,
    //       pointerRadius: 10,
    //       icon: "",
    //     } as Step,
    //     {
    //       selector: "[data-step='navigation']",
    //       title: t("tours.onboardingTour.steps.navigation.title"),
    //       content: t("tours.onboardingTour.steps.navigation.content"),
    //       side: "bottom",
    //       hideControls: false,
    //       pointerPadding: 6,
    //       pointerRadius: 10,
    //       icon: "",
    //     } as Step,
    //   ],
    // },
    // {
    //   tour: "homeTour",
    //   steps: [
    //     {
    //       // selector: "[data-step='basecamp-selector']",
    //       title: t("tours.homeTour.steps.intro.title"),
    //       content: t("tours.homeTour.steps.intro.content"),
    //       side: "top",
    //       hideControls: false,
    //       pointerPadding: 12,
    //       pointerRadius: 16,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //     {
    //       selector: "[data-step='basecamp-selector']",
    //       title: t("tours.homeTour.steps.basecamp.title"),
    //       content: t("tours.homeTour.steps.basecamp.content"),
    //       side: "top",
    //       hideControls: false,
    //       pointerPadding: 12,
    //       pointerRadius: 16,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //     {} as Step,
    //   ],
    // },
    // {
    //   tour: "basecampTour",
    //   steps: [
    //     {
    //       selector: "[data-step=’practice-mode-button’]",
    //       title: t("tours.basecampTour.steps.practiceMode.title"),
    //       content: t("tours.basecampTour.steps.practiceMode.content"),
    //       side: "top",
    //       hideControls: false,
    //       pointerPadding: 8,
    //       pointerRadius: 12,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //     {} as Step,
    //   ],
    // },
    {
      tour: "lobbyWelcomeTour",
      steps: [
        {
          title: t("tours.lobbyWelcomeTour.steps.welcome.title"),
          content: t("tours.lobbyWelcomeTour.steps.welcome.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='lobby-modes-nav']",
          title: t("tours.lobbyWelcomeTour.steps.playButton.title"),
          content: t("tours.lobbyWelcomeTour.steps.playButton.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='lobby-practice-mode-card']",
          title: t("tours.lobbyWelcomeTour.steps.practiceMode.title"),
          content: t("tours.lobbyWelcomeTour.steps.practiceMode.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {} as Step, // step 3: sentinel → navigates to practice
        {
          selector: "[data-step='back-to-home-button']",
          title: t("tours.lobbyWelcomeTour.steps.backToHome.title"),
          content: t("tours.lobbyWelcomeTour.steps.backToHome.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
      ],
    },
    {
      tour: "lobbySquadTour",
      steps: [
        {
          title: t("tours.lobbySquadTour.steps.intro.title"),
          content: t("tours.lobbySquadTour.steps.intro.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='lobby-equip-squad-slot']",
          title: t("tours.lobbySquadTour.steps.squadNav.title"),
          content: t("tours.lobbySquadTour.steps.squadNav.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='equip-token-list']",
          title: t("tours.lobbySquadTour.steps.tokenList.title"),
          content: t("tours.lobbySquadTour.steps.tokenList.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 8,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='equip-direction-toggle']",
          title: t("tours.lobbySquadTour.steps.direction.title"),
          content: t("tours.lobbySquadTour.steps.direction.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='equip-button']",
          title: t("tours.lobbySquadTour.steps.equip.title"),
          content: t("tours.lobbySquadTour.steps.equip.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
      ],
    },
    {
      tour: "createSquadTour",
      steps: [
        {
          selector: "[data-step='squad-slots']",
          title: t("tours.createSquadTour.steps.squadSlots.title"),
          content: t("tours.createSquadTour.steps.squadSlots.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='squad-name-input']",
          title: t("tours.createSquadTour.steps.squadName.title"),
          content: t("tours.createSquadTour.steps.squadName.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='create-squad-submit']",
          title: t("tours.createSquadTour.steps.createButton.title"),
          content: t("tours.createSquadTour.steps.createButton.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
      ],
    },
    {
      tour: "practiceTour",
      steps: [
        {
          title: t("tours.practiceTour.steps.init.title"),
          content: t("tours.practiceTour.steps.init.line1"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='practice-prizes']",
          title: t("tours.practiceTour.steps.prizes.title"),
          content: t("tours.practiceTour.steps.prizes.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='practice-controller']",
          title: t("tours.practiceTour.steps.controller.title"),
          content: t("tours.practiceTour.steps.controller.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        // {
        //   selector: "[data-step='practice-squad-slots']",
        //   title: t("tours.practiceTour.steps.squadSlots.title"),
        //   content: t("tours.practiceTour.steps.squadSlots.content"),
        //   side: "top",
        //   hideControls: false,
        //   pointerPadding: 10,
        //   pointerRadius: 12,
        //   showSkip: true,
        //   icon: "",
        // } as Step,
        {
          selector: "[data-step='direction-toggle']",
          title: t("tours.practiceTour.steps.directionToggle.title"),
          content: t("tours.practiceTour.steps.directionToggle.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='find-match-button']",
          title: t("tours.practiceTour.steps.findMatch.title"),
          content: t("tours.practiceTour.steps.findMatch.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='find-match-button']",
          title: t("tours.practiceTour.steps.findMatch2.title"),
          content: t("tours.practiceTour.steps.findMatch2.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
      ],
    },
    // {
    //   tour: "practiceDirectionTour",
    //   steps: [
    //     {
    //       selector: "[data-step=’direction-toggle’]",
    //       title: t("tours.practiceDirectionTour.steps.directionToggle.title"),
    //       content: t("tours.practiceDirectionTour.steps.directionToggle.content"),
    //       side: "bottom",
    //       hideControls: false,
    //       pointerPadding: 8,
    //       pointerRadius: 12,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //     {
    //       selector: "[data-step=’find-match-button’]",
    //       title: t("tours.practiceDirectionTour.steps.findMatch.title"),
    //       content: t("tours.practiceDirectionTour.steps.findMatch.content"),
    //       side: "top",
    //       hideControls: false,
    //       pointerPadding: 8,
    //       pointerRadius: 12,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //   ],
    // },
    // {
    //   tour: "practiceCompletionTour",
    //   steps: [
    //     {
    //       selector: "[data-step=’practice-complete’]",
    //       title: t("tours.practiceCompletionTour.steps.practiceComplete.title"),
    //       content: t("tours.practiceCompletionTour.steps.practiceComplete.content"),
    //       side: "top",
    //       hideControls: false,
    //       pointerPadding: 10,
    //       pointerRadius: 12,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //     {
    //       selector: "[data-step=’practice-complete-navigation’]",
    //       title: t("tours.practiceCompletionTour.steps.practiceCompleteNavigation.title"),
    //       content: t("tours.practiceCompletionTour.steps.practiceCompleteNavigation.content"),
    //       side: "top",
    //       hideControls: false,
    //       pointerPadding: 10,
    //       pointerRadius: 12,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //     {
    //       // selector: "[data-step=’practice-complete-navigation’]",
    //       title: t("tours.practiceCompletionTour.steps.goHome.title"),
    //       content: t("tours.practiceCompletionTour.steps.goHome.content"),
    //       side: "top",
    //       hideControls: false,
    //       pointerPadding: 10,
    //       pointerRadius: 12,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //   ],
    // },
    // {
    //   tour: "homeModesTour",
    //   steps: [
    //     {
    //       selector: "[data-step=’home-mode-7’]",
    //       title: t("tours.homeModesTour.steps.homeMode7.title"),
    //       content: t("tours.homeModesTour.steps.homeMode7.content"),
    //       side: "right",
    //       hideControls: false,
    //       pointerPadding: 12,
    //       pointerRadius: 14,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //     {
    //       selector: "[data-step=’home-mode-3’]",
    //       title: t("tours.homeModesTour.steps.homeMode3.title"),
    //       content: t("tours.homeModesTour.steps.homeMode3.content"),
    //       side: "right",
    //       hideControls: false,
    //       pointerPadding: 12,
    //       pointerRadius: 14,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //     {
    //       selector: "[data-step=’home-mode-10’]",
    //       title: t("tours.homeModesTour.steps.homeMode10.title"),
    //       content: t("tours.homeModesTour.steps.homeMode10.content"),
    //       side: "right",
    //       hideControls: false,
    //       pointerPadding: 12,
    //       pointerRadius: 14,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //     {
    //       selector: "[data-step=’home-mode-4’]",
    //       title: t("tours.homeModesTour.steps.homeMode4.title"),
    //       content: t("tours.homeModesTour.steps.homeMode4.content"),
    //       side: "top",
    //       hideControls: false,
    //       pointerPadding: 12,
    //       pointerRadius: 14,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //     {
    //       selector: "[data-step=’home-mode-6’]",
    //       title: t("tours.homeModesTour.steps.homeMode6a.title"),
    //       content: t("tours.homeModesTour.steps.homeMode6a.content"),
    //       side: "top",
    //       hideControls: false,
    //       pointerPadding: 12,
    //       pointerRadius: 14,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //     {
    //       selector: "[data-step=’home-mode-6’]",
    //       title: t("tours.homeModesTour.steps.homeMode6b.title"),
    //       content: t("tours.homeModesTour.steps.homeMode6b.content"),
    //       side: "top",
    //       hideControls: false,
    //       pointerPadding: 12,
    //       pointerRadius: 14,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //   ],
    // },
    // {
    //   tour: "practiceLiveTour",
    //   steps: [
    //     {
    //       selector: "[data-step=’practice-live-chart’]",
    //       title: t("tours.practiceLiveTour.steps.practiceLiveChart.title"),
    //       content: t("tours.practiceLiveTour.steps.practiceLiveChart.content"),
    //       side: "top",
    //       hideControls: false,
    //       pointerPadding: 10,
    //       pointerRadius: 12,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //     {
    //       selector: "[data-step=’practice-live-countdown’]",
    //       title: t("tours.practiceLiveTour.steps.practiceLiveCountdown.title"),
    //       content: t("tours.practiceLiveTour.steps.practiceLiveCountdown.content"),
    //       side: "bottom",
    //       hideControls: false,
    //       pointerPadding: 10,
    //       pointerRadius: 12,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //     {
    //       selector: "[data-step=’practice-live-finish’]",
    //       title: t("tours.practiceLiveTour.steps.practiceLiveFinish.title"),
    //       content: t("tours.practiceLiveTour.steps.practiceLiveFinish.content"),
    //       side: "top",
    //       hideControls: false,
    //       pointerPadding: 10,
    //       pointerRadius: 12,
    //       showSkip: true,
    //       icon: "",
    //     } as Step,
    //     // {
    //     //   selector: "[data-step=’practice-live-finish’]",
    //     //   title: "Wrap-up",
    //     //   content:
    //     //     "When the timer ends, you’ll jump to results automatically. Top squad wins; everyone sees final ranks and trophies.",
    //     //   side: "top",
    //     //   hideControls: false,
    //     //   pointerPadding: 10,
    //     //   pointerRadius: 12,
    //     //   showSkip: true,
    //     //   icon: "",
    //     // } as Step,
    //   ],
    // },
    {
      tour: "rushWaitingTour",
      steps: [
        {
          title:
            rushTourMode === "grand-rush"
              ? t("tours.rushWaitingTour.steps.init.grandRushTitle")
              : t("tours.rushWaitingTour.steps.init.title"),
          content: (
            <div>
              <p>
                {rushTourMode === "grand-rush"
                  ? t("tours.rushWaitingTour.steps.init.grandRushLine1")
                  : t("tours.rushWaitingTour.steps.init.line1")}
              </p>
              {/*
              <p className="mt-1.5">
                {t("tours.rushWaitingTour.steps.init.readDocsPrefix")}{" "}
                <a
                  href={
                    rushTourMode === "grand-rush"
                      ? "https://crypto-fantasy-league-1.gitbook.io/crypto-fantasy-league-docs/modes/rush/grand-rush"
                      : "https://crypto-fantasy-league-1.gitbook.io/crypto-fantasy-league-docs/modes/rush"
                  }
                  target="_blank"
                  className="underline text-blue-800"
                >
                  {t("tours.rushWaitingTour.steps.init.readDocsLink")}
                </a>
              </p>
              */}
            </div>
          ),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='rush-timer']",
          title: t("tours.rushWaitingTour.steps.rushTimer.title"),
          content:
            rushTourMode === "grand-rush"
              ? t("tours.rushWaitingTour.steps.rushTimer.grandRushContent")
              : rushTourMode === "rookie-rush"
                ? t("tours.rushWaitingTour.steps.rushTimer.rookieRushContent")
                : t("tours.rushWaitingTour.steps.rushTimer.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='rush-prizes']",
          title: t("tours.rushWaitingTour.steps.rushPrizes.title"),
          content: t("tours.rushWaitingTour.steps.rushPrizes.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        ...(rushTourMode !== "rookie-rush"
          ? [
              {
                selector: "[data-step='rush-daily']",
                title:
                  rushTourMode === "grand-rush"
                    ? t("tours.rushWaitingTour.steps.rushDaily.grandRushTitle")
                    : t("tours.rushWaitingTour.steps.rushDaily.title"),
                content:
                  rushTourMode === "grand-rush"
                    ? t(
                        "tours.rushWaitingTour.steps.rushDaily.grandRushContent",
                      )
                    : t("tours.rushWaitingTour.steps.rushDaily.content"),
                side: "bottom",
                hideControls: false,
                pointerPadding: 10,
                pointerRadius: 12,
                showSkip: true,
                icon: "",
              } as Step,
            ]
          : []),
        // NOTE: Duplicated
        // {
        //   selector: "[data-step='rush-daily']",
        //   title: t("tours.rushWaitingTour.steps.rushDaily2.title"),
        //   content: t("tours.rushWaitingTour.steps.rushDaily2.content"),
        //   side: "bottom",
        //   hideControls: false,
        //   pointerPadding: 10,
        //   pointerRadius: 12,
        //   showSkip: true,
        //   icon: "",
        // } as Step,
        // {
        //   selector: "[data-step='rush-controller']",
        //   title: t("tours.rushWaitingTour.steps.rushController.title"),
        //   content: t("tours.rushWaitingTour.steps.rushController.content"),
        //   side: "top",
        //   hideControls: false,
        //   pointerPadding: 10,
        //   pointerRadius: 12,
        //   showSkip: true,
        //   icon: "",
        // } as Step,
        {
          selector: "[data-step='rush-squad-slots']",
          title: t("tours.rushWaitingTour.steps.rushSquadSlots.title"),
          content: t("tours.rushWaitingTour.steps.rushSquadSlots.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='rush-auto-join']",
          title: t("tours.rushWaitingTour.steps.rushAutoJoin.title"),
          content: t("tours.rushWaitingTour.steps.rushAutoJoin.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        // {
        //   selector: "[data-step='rush-join']",
        //   title: t("tours.rushWaitingTour.steps.rushJoin.title"),
        //   content: t("tours.rushWaitingTour.steps.rushJoin.content"),
        //   side: "top",
        //   hideControls: false,
        //   pointerPadding: 10,
        //   pointerRadius: 12,
        //   showSkip: true,
        //   icon: "",
        // } as Step,
        // {
        //   selector: "[data-step='rush-join']",
        //   title: t("tours.rushWaitingTour.steps.rushJoin2.title"),
        //   content: t("tours.rushWaitingTour.steps.rushJoin2.content"),
        //   side: "top",
        //   hideControls: false,
        //   pointerPadding: 10,
        //   pointerRadius: 12,
        //   showSkip: true,
        //   icon: "",
        // } as Step,
      ],
    },
    {
      tour: "rushExtraTrophyTour",
      steps: [
        {
          selector: "[data-step='rush-prizes']",
          title: t("tours.rushExtraTrophyTour.steps.rushExtraTrophy.title"),
          content: t("tours.rushExtraTrophyTour.steps.rushExtraTrophy.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
      ],
    },
    {
      tour: "grandRushExtraTrophyTour",
      steps: [
        {
          selector: "[data-step='rush-prizes']",
          title: t(
            "tours.grandRushExtraTrophyTour.steps.grandRushExtraTrophy.title",
          ),
          content: t(
            "tours.grandRushExtraTrophyTour.steps.grandRushExtraTrophy.content",
          ),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
      ],
    },
    {
      tour: "rushExtraTrophyClaimedTour",
      steps: [
        {
          title: t(
            "tours.rushExtraTrophyClaimedTour.steps.rushExtraTrophyClaimed.title",
          ),
          content: t(
            "tours.rushExtraTrophyClaimedTour.steps.rushExtraTrophyClaimed.content",
          ),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
      ],
    },
    {
      tour: "grandRushExtraTrophyClaimedTour",
      steps: [
        {
          title: t(
            "tours.grandRushExtraTrophyClaimedTour.steps.rushExtraTrophyClaimed.title",
          ),
          content: t(
            "tours.grandRushExtraTrophyClaimedTour.steps.rushExtraTrophyClaimed.content",
          ),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
      ],
    },
    {
      tour: "welcomeProTour",
      steps: [
        {
          title: t("tours.welcomeProTour.steps.welcome.title"),
          content: t("tours.welcomeProTour.steps.welcome.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: false,
          icon: "",
        } as Step,
      ],
    },
    {
      tour: "questPanelTour",
      steps: [
        {
          selector: "[data-step='quest-panel']",
          title: t("tours.questPanelTour.steps.questPanel.title"),
          content: t("tours.questPanelTour.steps.questPanel.content"),
          side: "bottom",
          hideControls: true,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='quest-panel-cta']",
          title: t("tours.questPanelTour.steps.questPanelCta.title"),
          content: t("tours.questPanelTour.steps.questPanelCta.content"),
          side: "bottom",
          hideControls: true,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
      ],
    },
    {
      tour: "wtaTour",
      steps: [
        {
          title: t("tours.wtaTour.steps.init.title"),
          content: (
            <div>
              <p>{t("tours.wtaTour.steps.init.line1")}</p>
            </div>
          ),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='wta-room-pro']",
          title: t("tours.wtaTour.steps.wtaRoomPro.title"),
          content: t("tours.wtaTour.steps.wtaRoomPro.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='wta-room-semi-pro']",
          title: t("tours.wtaTour.steps.wtaRoomSemiPro.title"),
          content: t("tours.wtaTour.steps.wtaRoomSemiPro.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='wta-room-amateur']",
          title: t("tours.wtaTour.steps.wtaRoomAmateur.title"),
          content: t("tours.wtaTour.steps.wtaRoomAmateur.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          title: t("tours.wtaTour.steps.wtaMultiEntry.title"),
          content: t("tours.wtaTour.steps.wtaMultiEntry.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
      ],
    },
    {
      tour: "alphaDomeTour",
      steps: [
        {
          title: t("tours.alphaDomeTour.steps.init.title"),
          content: (
            <div>
              <p>{t("tours.alphaDomeTour.steps.init.line1")}</p>
            </div>
          ),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='alpha-dome-create']",
          title: t("tours.alphaDomeTour.steps.alphaDomeCreate1.title"),
          content: t("tours.alphaDomeTour.steps.alphaDomeCreate1.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='alpha-dome-create']",
          title: t("tours.alphaDomeTour.steps.alphaDomeCreate2.title"),
          content: t("tours.alphaDomeTour.steps.alphaDomeCreate2.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='alpha-dome-create']",
          title: t("tours.alphaDomeTour.steps.alphaDomeCreate3.title"),
          content: t("tours.alphaDomeTour.steps.alphaDomeCreate3.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          title: t("tours.alphaDomeTour.steps.alphaDomeList.title"),
          content: t("tours.alphaDomeTour.steps.alphaDomeList.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
      ],
    },
    {
      tour: "luckyDrawTour",
      steps: [
        {
          title: t("tours.luckyDrawTour.steps.init.title"),
          content: (
            <div>
              <p>{t("tours.luckyDrawTour.steps.init.line1")}</p>
            </div>
          ),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='lucky-draw-prize']",
          title: t("tours.luckyDrawTour.steps.luckyDrawPrize.title"),
          content: t("tours.luckyDrawTour.steps.luckyDrawPrize.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='lucky-draw-box']",
          title: t("tours.luckyDrawTour.steps.luckyDrawBox.title"),
          content: t("tours.luckyDrawTour.steps.luckyDrawBox.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='lucky-draw-earn']",
          title: t("tours.luckyDrawTour.steps.luckyDrawEarn.title"),
          content: t("tours.luckyDrawTour.steps.luckyDrawEarn.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='lucky-draw-tickets']",
          title: t("tours.luckyDrawTour.steps.luckyDrawTickets.title"),
          content: t("tours.luckyDrawTour.steps.luckyDrawTickets.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='lucky-draw-schedule']",
          title: t("tours.luckyDrawTour.steps.luckyDrawSchedule.title"),
          content: (
            <div>
              <p>{t("tours.luckyDrawTour.steps.luckyDrawSchedule.line1")}</p>
            </div>
          ),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
      ],
    },
    {
      tour: "vipProgramTour",
      steps: [
        {
          title: t("tours.vipProgramTour.steps.init.title"),
          content: (
            <div>
              <p>{t("tours.vipProgramTour.steps.init.line1")}</p>
            </div>
          ),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='vip-tiers']",
          title: t("tours.vipProgramTour.steps.tiers.title"),
          content: t("tours.vipProgramTour.steps.tiers.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='vip-progress']",
          title: t("tours.vipProgramTour.steps.progress.title"),
          content: t("tours.vipProgramTour.steps.progress.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='vip-cashback']",
          title: t("tours.vipProgramTour.steps.cashback.title"),
          content: t("tours.vipProgramTour.steps.cashback.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='vip-accordion']",
          title: t("tours.vipProgramTour.steps.accordion.title"),
          content: t("tours.vipProgramTour.steps.accordion.content"),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='vip-rates']",
          title: t("tours.vipProgramTour.steps.rates.title"),
          content: t("tours.vipProgramTour.steps.rates.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          title: t("tours.vipProgramTour.steps.outro.title"),
          content: (
            <div>
              <p>{t("tours.vipProgramTour.steps.outro.line1")}</p>
            </div>
          ),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
      ],
    },
    {
      tour: "kothWaitingTour",
      steps: [
        {
          title: t("tours.kothWaitingTour.steps.init.title"),
          content: (
            <div>
              <p>{t("tours.kothWaitingTour.steps.init.line1")}</p>
            </div>
          ),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          title: t("tours.kothWaitingTour.steps.kothStreakMechanic.title"),
          content: t("tours.kothWaitingTour.steps.kothStreakMechanic.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='koth-state']",
          title: t("tours.kothWaitingTour.steps.kothController.title"),
          content:
            kothTourMode === "rookie-koth"
              ? t("tours.kothWaitingTour.steps.kothControllerRookie.content")
              : t("tours.kothWaitingTour.steps.kothController.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='daily-rewards']",
          title: t("tours.kothWaitingTour.steps.kothLeaderboards.title"),
          content: t("tours.kothWaitingTour.steps.kothLeaderboards.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='koth-streak']",
          title: t("tours.kothWaitingTour.steps.kothStreak.title"),
          content: kothTourMode === "rookie-koth"
            ? t("tours.kothWaitingTour.steps.kothStreakRookie.content")
            : t("tours.kothWaitingTour.steps.kothStreak.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='koth-daily']",
          title: t("tours.kothWaitingTour.steps.kothDaily.title"),
          content: kothTourMode === "rookie-koth"
            ? t("tours.kothWaitingTour.steps.kothDailyRookie.content")
            : t("tours.kothWaitingTour.steps.kothDaily.content"),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
      ],
    },
    {
      tour: "championshipInactiveTour",
      steps: [
        {
          title: t("tours.championshipInactiveTour.steps.init.title"),
          content: (
            <div>
              <p>{t("tours.championshipInactiveTour.steps.init.line1")}</p>
            </div>
          ),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
        {
          selector: "[data-step='championship-nav']",
          title: t(
            "tours.championshipInactiveTour.steps.championshipNav.title",
          ),
          content: t(
            "tours.championshipInactiveTour.steps.championshipNav.content",
          ),
          side: "top",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
      ],
    },
    {
      tour: "championshipTour",
      steps: [
        ...(championshipTourMode === "qualifier"
          ? [
              {
                title: t("tours.championshipTour.steps.qualifierInit.title"),
                content: (
                  <div>
                    <p>
                      {t("tours.championshipTour.steps.qualifierInit.line1")}
                    </p>
                  </div>
                ),
                side: "bottom",
                hideControls: false,
                pointerPadding: 10,
                pointerRadius: 12,
                showSkip: true,
                icon: "",
              } as Step,
              {
                title: t("tours.championshipTour.steps.qualifierList.title"),
                content: t(
                  "tours.championshipTour.steps.qualifierList.content",
                ),
                side: "top",
                hideControls: false,
                pointerPadding: 10,
                pointerRadius: 12,
                showSkip: true,
                icon: "",
              } as Step,
            ]
          : [
              {
                title: t("tours.championshipTour.steps.bracketInit.title"),
                content: (
                  <div>
                    <p>{t("tours.championshipTour.steps.bracketInit.line1")}</p>
                  </div>
                ),
                side: "bottom",
                hideControls: false,
                pointerPadding: 10,
                pointerRadius: 12,
                showSkip: true,
                icon: "",
              } as Step,
              {
                title: t("tours.championshipTour.steps.bracketSquad.title"),
                content: t("tours.championshipTour.steps.bracketSquad.content"),
                side: "top",
                hideControls: false,
                pointerPadding: 10,
                pointerRadius: 12,
                showSkip: true,
                icon: "",
              } as Step,
            ]),
        {
          selector: "[data-step='championship-bracket']",
          title: t(
            championshipTourMode === "qualifier"
              ? "tours.championshipTour.steps.qualifierRewards.title"
              : "tours.championshipTour.steps.bracketTiming.title",
          ),
          content: t(
            championshipTourMode === "qualifier"
              ? "tours.championshipTour.steps.qualifierRewards.content"
              : "tours.championshipTour.steps.bracketTiming.content",
          ),
          side: "bottom",
          hideControls: false,
          pointerPadding: 10,
          pointerRadius: 12,
          showSkip: true,
          icon: "",
        } as Step,
      ],
    },
  ];

  return (
    <NextStepProvider>
      <TourSteps navigate={navigate} steps={steps}>
        {children}
      </TourSteps>
    </NextStepProvider>
  );
}
